import { HTMLAttributes, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Table } from 'antd';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { up } from 'styled-breakpoints';
import styled, { useTheme } from 'styled-components';
import { match, P } from 'ts-pattern';
import {
  MetricCallAnalysisCallsStatusObject,
  Query,
  QueryMetricSumCallAnalysisArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import { TableColumns } from 'src/components/general/display/table';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  Card,
  CardBaseContent,
  CardMainNumericValue,
  ColoredDotLabel,
  CompareBadge,
  emptyValuePlaceholder,
  SimpleDashboardTable,
  useCardValueHelpers,
  DashboardTableColumnTitle,
  DashboardTableCategoryPercentLabel,
  DashboardTableItemValue,
  getDashboardPercentageDisplayValue,
  DashboardTableEmptyValue,
} from 'src/components/pages/dashboard/components/shared';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export enum MetricSummaryCallAnalysisCallStatus {
  ANSWERED = 'ANSWERED',
  MISSED = 'MISSED',
  PLACED = 'PLACED',
  VOICEMAIL = 'VOICEMAIL',
  ABANDONED = 'ABANDONED',
}

export const GET_METRIC_CALL_ANALYSIS_QUERY = gql`
  query GetMetricSumCallAnalysis(
    $institutionId: Int!
    $period: MetricPeriodWhereInput!
    $comparePeriod: MetricComparePeriodInput
    $where: MetricSumCallAnalysisWhereInput
  ) {
    metricSumCallAnalysis(
      institutionId: $institutionId
      period: $period
      comparePeriod: $comparePeriod
      where: $where
    ) {
      total
      callsStatuses {
        total
        status
        previousPeriod {
          total
          trendChangePercentage
          trendChangeIndicator
        }
      }
      previousPeriod {
        total
        trendChangePercentage
        trendChangeIndicator
      }
      lastUpdatedAt
    }
  }
`;

const CallAnalysisSummaryContainer = styled(FlexRow)`
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TotalCallsTitle = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.39px;
`;

const TotalCallsValue = styled.span`
  margin-right: 10px;
`;

const PreviousPeriodValue = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 11px;
  line-height: 14px;
  letter-spacing: -0.11px;
`;

const TableColumnTitle = styled(DashboardTableColumnTitle)`
  display: inline-block;
`;

const PreviousPeriodBadgeContainer = styled(FlexRow)`
  justify-content: flex-end;
`;

const CardContentContainer = styled(CardBaseContent)`
  padding: 15px 20px 20px;
  height: 343px;

  ${up('md')} {
    height: 357px;
  }
`;

const EmptyCardContentContainer = styled(CardContentContainer)`
  padding: 0;
`;

const TotalCallSummaryContainer = styled(FlexRow)`
  align-items: center;
`;

const StyledTable = styled(SimpleDashboardTable)`
  margin-top: 16px;

  .ant-table-cell:nth-child(2) {
    text-align: right;
  }

  .ant-table.ant-table-small .ant-table-tbody > tr > td,
  .ant-table.ant-table-small .ant-table-thead > tr > th {
    line-height: 18px;
    padding: 9.5px 8px;
  }

  .ant-table.ant-table-small .ant-table-thead > tr > th {
    padding-top: 0;
  }
` as typeof Table;

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  overflow: hidden;
  border-radius: 10px;

  .recharts-wrapper svg {
    transform: scale(1.01, 1.5);
  }
`;

type StackedChartData = {
  answered: number;
  missed: number;
  outgoing: number;
  voicemail: number;
  abandoned: number;
};

export const CallAnalysisCard = () => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { t } = useTranslation(['common', 'dashboard']);
  const { targetUserId, metricPeriodWhereInput, metricComparePeriodWhereInput } =
    useContext(DashboardFiltersContext);
  const theme = useTheme();
  const { getNumericValueAbbreviation } = useCardValueHelpers();

  const getCallStatusLabel = useCallback(
    (label: string) =>
      match(label)
        .with(MetricSummaryCallAnalysisCallStatus.ANSWERED, () =>
          t('dashboard:cards.callAnalysis.callStatuses.answered'),
        )
        .with(MetricSummaryCallAnalysisCallStatus.ABANDONED, () =>
          t('dashboard:cards.callAnalysis.callStatuses.abandoned'),
        )
        .with(MetricSummaryCallAnalysisCallStatus.MISSED, () =>
          t('dashboard:cards.callAnalysis.callStatuses.missed'),
        )
        .with(MetricSummaryCallAnalysisCallStatus.PLACED, () =>
          t('dashboard:cards.callAnalysis.callStatuses.outgoing'),
        )
        .with(MetricSummaryCallAnalysisCallStatus.VOICEMAIL, () =>
          t('dashboard:cards.callAnalysis.callStatuses.voicemail'),
        )
        .otherwise(() => ''),
    [t],
  );

  const getCallStatusColor = useCallback(
    (callStatus: MetricSummaryCallAnalysisCallStatus) => {
      return match(callStatus)
        .with(
          MetricSummaryCallAnalysisCallStatus.ANSWERED,
          () => theme.colors.secondaryMint60,
        )
        .with(
          MetricSummaryCallAnalysisCallStatus.MISSED,
          () => theme.colors.secondaryPeriwinkle60,
        )
        .with(MetricSummaryCallAnalysisCallStatus.PLACED, () => '#FFD566')
        .with(
          MetricSummaryCallAnalysisCallStatus.VOICEMAIL,
          () => theme.colors.secondaryTopaz60,
        )
        .with(
          MetricSummaryCallAnalysisCallStatus.ABANDONED,
          () => theme.colors.secondaryFlower60,
        )
        .otherwise(() => '');
    },
    [theme],
  );

  const metricCallAnalysisQueryResult = useQuery<
    Pick<Query, 'metricSumCallAnalysis'>,
    Partial<QueryMetricSumCallAnalysisArgs>
  >(GET_METRIC_CALL_ANALYSIS_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      period: metricPeriodWhereInput,
      comparePeriod: metricComparePeriodWhereInput,
      where: whenDefined(targetUserId, (id) => ({
        dimensionParticipatingUser: { id: { _eq: id } },
      })),
    },
    onError: handleGraphQLError,
  });

  const { data, loading } = metricCallAnalysisQueryResult;

  const metricSumCallAnalysisData = data?.metricSumCallAnalysis;

  const {
    total = null,
    previousPeriod,
    lastUpdatedAt,
    callsStatuses = [],
  } = metricSumCallAnalysisData ?? {};

  const stackedChartData: StackedChartData = useMemo(() => {
    return callsStatuses.reduce(
      (prev, curr) => {
        return match(curr)
          .with(
            {
              status: MetricSummaryCallAnalysisCallStatus.ANSWERED,
              total: P.not(P.nullish),
            },
            ({ total }) => {
              prev.answered = total;
              return prev;
            },
          )
          .with(
            {
              status: MetricSummaryCallAnalysisCallStatus.ABANDONED,
              total: P.not(P.nullish),
            },
            ({ total }) => {
              prev.abandoned = total;
              return prev;
            },
          )
          .with(
            {
              status: MetricSummaryCallAnalysisCallStatus.MISSED,
              total: P.not(P.nullish),
            },
            ({ total }) => {
              prev.missed = total;
              return prev;
            },
          )
          .with(
            {
              status: MetricSummaryCallAnalysisCallStatus.PLACED,
              total: P.not(P.nullish),
            },
            ({ total }) => {
              prev.outgoing = total;
              return prev;
            },
          )
          .with(
            {
              status: MetricSummaryCallAnalysisCallStatus.VOICEMAIL,
              total: P.not(P.nullish),
            },
            ({ total }) => {
              prev.voicemail = total;
              return prev;
            },
          )
          .otherwise(() => prev);
      },
      {
        abandoned: 0,
        answered: 0,
        missed: 0,
        outgoing: 0,
        voicemail: 0,
      } as StackedChartData,
    );
  }, [callsStatuses]);

  const columns: TableColumns<MetricCallAnalysisCallsStatusObject>[] = useMemo(
    () => [
      {
        key: 'status',
        title: (
          <TableColumnTitle data-lgg-id="table-status-column">
            {t('dashboard:cards.callAnalysis.details.columns.status')}
          </TableColumnTitle>
        ),
        dataIndex: 'status',
        render: (value) => (
          <ColoredDotLabel
            color={getCallStatusColor(value)}
            value={getCallStatusLabel(value)}
          />
        ),
      },
      {
        key: 'total',
        title: (
          <TableColumnTitle
            data-lgg-id="table-total-calls-column"
            style={{ textAlign: 'right' }}
          >
            {t('dashboard:cards.callAnalysis.details.columns.totalCalls')}
          </TableColumnTitle>
        ),
        dataIndex: 'total',
        width: '105px',
        render: (value, data) => {
          return (
            <PreviousPeriodBadgeContainer>
              <DashboardTableItemValue data-lgg-id="table-detail-value">
                {getNumericValueAbbreviation(value)}
              </DashboardTableItemValue>
              <DashboardTableCategoryPercentLabel>
                {getDashboardPercentageDisplayValue(total ?? 0, value)}
              </DashboardTableCategoryPercentLabel>
            </PreviousPeriodBadgeContainer>
          );
        },
      },
      {
        key: 'previous-period',
        title: (
          <TableColumnTitle>
            {t('dashboard:cards.callAnalysis.details.columns.prevPeriod')}
          </TableColumnTitle>
        ),
        dataIndex: 'previousPeriod',
        width: '78px',
        render: (value, data) => (
          <PreviousPeriodBadgeContainer>
            {data.previousPeriod ? (
              <CompareBadge
                testId="table-detail-value-difference"
                trendChangePercentage={data.previousPeriod?.trendChangePercentage}
                trendChangeIndicator={
                  data.previousPeriod?.trendChangeIndicator ?? 'NEUTRAL'
                }
                fromValue={data.previousPeriod?.total.toLocaleString()}
                toValue={data.total?.toLocaleString()}
                variant="compact"
                forceEmptyState={data.previousPeriod?.total === 0}
              />
            ) : (
              <DashboardTableEmptyValue />
            )}
          </PreviousPeriodBadgeContainer>
        ),
      },
    ],
    [t, getCallStatusColor, getCallStatusLabel, getNumericValueAbbreviation, total],
  );

  return (
    <Card loading={loading} data-lgg-id="dashboard-call-analysis-card">
      <Card.Header
        title={t('dashboard:cards.callAnalysis.title')}
        updatedAt={lastUpdatedAt}
        cardInfoI18nKey="dashboard:cards.callAnalysis.tooltipDescription"
        cardTextTitle={t('dashboard:cards.callAnalysis.title')}
      />
      <Card.Content>
        {total ? (
          <CardContentContainer>
            <FlexColumn>
              <CallAnalysisSummaryContainer>
                <FlexColumn>
                  <TotalCallsTitle>
                    {t('dashboard:cards.callAnalysis.totalCalls')}
                  </TotalCallsTitle>
                  <TotalCallSummaryContainer>
                    <TotalCallsValue data-lgg-id="call-analysis-total-value">
                      <CardMainNumericValue value={total} />
                    </TotalCallsValue>
                    {previousPeriod?.trendChangePercentage !== undefined ? (
                      <CompareBadge
                        trendChangePercentage={previousPeriod.trendChangePercentage}
                        trendChangeIndicator={previousPeriod?.trendChangeIndicator}
                        fromValue={previousPeriod.total.toLocaleString()}
                        toValue={total.toLocaleString()}
                        testId="call-analysis-compared-to"
                        variant="compact"
                      />
                    ) : null}
                  </TotalCallSummaryContainer>
                </FlexColumn>
                <FlexColumn>
                  <PreviousPeriodValue data-lgg-id="call-analysis-previous-value">{`${t(
                    'dashboard:cards.previousPeriod',
                  )}: ${
                    previousPeriod?.total !== undefined
                      ? getNumericValueAbbreviation(previousPeriod.total)
                      : emptyValuePlaceholder
                  }`}</PreviousPeriodValue>
                </FlexColumn>
              </CallAnalysisSummaryContainer>
              <StyledResponsiveContainer width="100%" height={36}>
                <BarChart
                  data={[stackedChartData]}
                  style={{ stroke: theme.colors.white, strokeWidth: 2, padding: 0 }}
                  layout="vertical"
                  margin={{ bottom: 0, left: 0, right: 0, top: 0 }}
                  maxBarSize={36}
                  barSize={36}
                  barGap="100px"
                >
                  <XAxis hide type="number" domain={['dataMin', 'dataMax']} />
                  <YAxis hide type="category" domain={['dataMin', 'dataMax']} />
                  <Bar
                    dataKey="answered"
                    stackId="main"
                    fill={getCallStatusColor(
                      MetricSummaryCallAnalysisCallStatus.ANSWERED,
                    )}
                    alignmentBaseline="central"
                  />
                  <Bar
                    dataKey="missed"
                    stackId="main"
                    fill={getCallStatusColor(MetricSummaryCallAnalysisCallStatus.MISSED)}
                  />
                  <Bar
                    dataKey="outgoing"
                    stackId="main"
                    fill={getCallStatusColor(MetricSummaryCallAnalysisCallStatus.PLACED)}
                  />
                  <Bar
                    dataKey="voicemail"
                    stackId="main"
                    fill={getCallStatusColor(
                      MetricSummaryCallAnalysisCallStatus.VOICEMAIL,
                    )}
                  />
                  <Bar
                    dataKey="abandoned"
                    stackId="main"
                    fill={getCallStatusColor(
                      MetricSummaryCallAnalysisCallStatus.ABANDONED,
                    )}
                  />
                </BarChart>
              </StyledResponsiveContainer>
              <div>
                <StyledTable
                  data-lgg-id="call-analysis-card-details-table"
                  showSorterTooltip={false}
                  loading={loading}
                  columns={columns}
                  dataSource={callsStatuses}
                  pagination={false}
                  tableLayout="auto"
                  size="small"
                  rowKey={({ status }) => `call-analysis-card-details-table-${status}`}
                  rowClassName={(_, index) =>
                    `call-analysis-card-details-row-position-${index}`
                  }
                  onRow={({ status }) =>
                    ({
                      'data-lgg-id': `call-analysis-card-details-row-${status}`,
                    } as HTMLAttributes<HTMLElement>)
                  }
                />
              </div>
            </FlexColumn>
          </CardContentContainer>
        ) : (
          <EmptyCardContentContainer $isEmpty>
            <Card.EmptyState
              icon="barChart"
              title={t('dashboard:cards.callAnalysis.emptyState.title')}
              subTitle={t('dashboard:cards.callAnalysis.emptyState.subTitle')}
              testId="call-analysis-card-empty-state"
              iconVariant="large"
            />
          </EmptyCardContentContainer>
        )}
      </Card.Content>
    </Card>
  );
};
